if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = this.getCartElement();
        window.themeAssets?.loadGroup('cart');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        this.submitToCart();
      }

      getCartElement() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      async ensureCart() {
        this.cart = this.getCartElement();
        if (!this.cart || typeof this.cart.getSectionsToRender === 'function') return;

        await window.themeAssets?.loadGroup('cart');
        this.cart = this.getCartElement();
        if (typeof this.cart?.getSectionsToRender !== 'function') this.cart = null;
      }

      async submitToCart() {
        try {
          await this.ensureCart();
        } catch (error) {
          console.error(error);
          this.cart = null;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const giftPackaging = this.getGiftPackaging();
        const giftVariantId = giftPackaging?.selectedVariantId;
        const quantity = this.getQuantity();

        if (giftVariantId) {
          const properties = {};

          for (const [key, value] of formData.entries()) {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);
            if (propertyMatch && value !== '') properties[propertyMatch[1]] = value;
          }

          const body = {
            items: [
              {
                id: Number(formData.get('id')),
                quantity,
                ...(Object.keys(properties).length ? { properties } : {}),
              },
              { id: Number(giftVariantId), quantity },
            ],
          };

          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(body);
        } else {
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          delete config.headers['Content-Type'];
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });

              giftPackaging?.reset();
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const quickViewModal = document.getElementById('product-quick-view');
            
            if (quickViewModal) {
              quickViewModal.classList.remove('quick-view-loaded');
              document.body.classList.remove('quickview-open');
            }
            
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.dispatchEvent(new CustomEvent('product-form:complete', { bubbles: true }));
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.submitButton.hasAttribute('data-preorder') ? 
          window.variantStrings.preOrder : 
          window.variantStrings.addToCart;
          
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getQuantity() {
        const quantityInput = this.form.elements.namedItem('quantity');
        const value = parseInt(quantityInput?.value, 10);
        return Number.isNaN(value) ? 1 : value;
      }

      getGiftPackaging() {
        const root = this.closest('product-info') || this.closest('.shopify-section');
        return root?.querySelector('gift-packaging') || null;
      }
    }
  );
}
